import { useState, useEffect } from "react";
import moment from "moment";
import md5 from "md5";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { project, errorColor, bodyColor } from "../../../../config/constants";
import { parsedDate, isLessThanToday } from "../../../../config/functions";
import { isAISNProjectHospital } from "../../../../config/projectFunctions";
import { renderCivilStatusOptions, renderEducationOptions, renderWorkOptions } from "../../../../config/dropdownFunctions";
// Components
import {
  PersonalInformationDiv,
  TitleTextDiv,
  DataDiv,
  ColumnDataDiv,
  InputDataDiv,
  Label,
  Input,
  RelativeButton,
  InputStartingDateDiv,
  InputStartingDate,
  InputBirthday,
  Textarea,
  HandsContainer,
  SkinColorContainer,
  SkinColorCheckboxContainer,
  SkinColorCheckbox,
  SkinColorImg,
  SkinColorChecked,
} from "./styles";
import Select from "../../../selectInput";
// Assets
import SelectArrow from "../../../../assets/icons/ico_arrow_down.svg";
import VisiblePasswordImg from "../../../../assets/icons/btn_visible_password.svg";
import HidePasswordImg from "../../../../assets/icons/btn_hide_password.svg";
import CheckImg from "../../../../assets/images/ico_check.png";
import Hand1 from "../../../../assets/images/patients/ico_hand_skin1.png";
import Hand2 from "../../../../assets/images/patients/ico_hand_skin2.png";
import Hand3 from "../../../../assets/images/patients/ico_hand_skin3.png";
import Hand4 from "../../../../assets/images/patients/ico_hand_skin4.png";
import Hand5 from "../../../../assets/images/patients/ico_hand_skin5.png";

const PersonalInfo = ({
  disabledInputs,
  hospitalClinicians,
  user,
  password,
  setPassword,
  passwordError,
  setPasswordError,
  clinicianId,
  setClinicianId,
  clinicianIdError,
  setClinicianIdError,
  aisnGroup,
  currentStartingDate,
  startingDate,
  setStartingDate,
  startingDateError,
  setStartingDateError,
  gender,
  setGender,
  genderError,
  setGenderError,
  height,
  setHeight,
  heightError,
  setHeightError,
  dateOfBirth,
  setDateOfBirth,
  dateOfBirthError,
  setDateOfBirthError,
  civilStatus,
  setCivilStatus,
  civilStatusError,
  setCivilStatusError,
  homeSupport,
  setHomeSupport,
  homeSupportError,
  setHomeSupportError,
  education,
  setEducation,
  educationError,
  setEducationError,
  work,
  setWork,
  workError,
  setWorkError,
  gameExperience,
  setGameExperience,
  computerExperience,
  setComputerExperience,
  otherInfo,
  setOtherInfo,
  skinColor,
  setSkinColor,
  pareticSide,
  patientVersion,
  setPersonalInfo,
}) => {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [showButtonPassword, setShowButtonPassword] = useState(false);
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const [t] = useTranslation("global");

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName]);

  const handleSetPasswordVisibility = (e) => {
    e.preventDefault();
    setVisiblePassword(!visiblePassword);
  };

  const handleSetInput = (e, setInput, setError, input) => {
    setInput(e.target.value);
    setError && setError(false);
    setPersonalInfo({
      PASSWORD: input === "password" ? (e.target.value !== "" ? md5(`${user}${e.target.value}`) : e.target.value) : password,
      GENDER: input === "gender" ? e.target.value : gender,
      PTN_HEIGHT_CM: input === "height" ? Number(e.target.value) : Number(height),
      BIRTH_DATE:
        input === "birthDate"
          ? moment(parsedDate(e.target.value)).format("YYYY/MM/DD")
          : moment(parsedDate(dateOfBirth)).format("YYYY/MM/DD"),
      VIDEOGAME_EXP: input === "videoGameExp" ? e.target.value : gameExperience,
      COMPUTER_EXP: input === "computerExp" ? e.target.value : computerExperience,
      COMMENTS: input === "comment" ? e.target.value : otherInfo,
      SKIN_COLOR: input === "skinColor" ? e.target.value : skinColor,
      PARETIC_SIDE: pareticSide,
      VERSION: patientVersion,
    });
  };

  const handleShowPassword = (e, setPassword, setPasswordError) => {
    handleSetInput(e, setPassword, setPasswordError, "password");
    if (e.target.value.length) {
      setShowButtonPassword(true);
    } else {
      setShowButtonPassword(false);
    }
  };

  const renderSkinColor = () => {
    const renderSkinColorInput = (skinColorParam, skinImg) => {
      const verifySkinColor = skinColorParam === skinColor;

      return (
        <SkinColorCheckboxContainer verifySkinColor={verifySkinColor}>
          <SkinColorCheckbox
            type="checkbox"
            value={skinColorParam}
            disabled={disabledInputs}
            onClick={(e) => handleSetInput(e, setSkinColor, false, "skinColor")}
          />
          <SkinColorImg src={skinImg} alt="ico-hand" verifySkinColor={verifySkinColor} />
          {verifySkinColor && <SkinColorChecked src={CheckImg} alt="ico-check" />}
        </SkinColorCheckboxContainer>
      );
    };

    return (
      <SkinColorContainer>
        <InputDataDiv>
          <Label>{isAISNHospital ? "Avatar" : t("personalInformation_skinColorLabel")}</Label>
          <HandsContainer>
            {renderSkinColorInput("FDC3AD", Hand1)}
            {renderSkinColorInput("D09C80", Hand2)}
            {renderSkinColorInput("A37552", Hand3)}
            {renderSkinColorInput("6B4731", Hand4)}
            {renderSkinColorInput("33180F", Hand5)}
          </HandsContainer>
        </InputDataDiv>
      </SkinColorContainer>
    );
  };

  const renderSelectAISNGroup = () => {
    return (
      <InputDataDiv>
        <Label htmlFor="login" fontWeight="bold">
          {t("AISN Group")} *
        </Label>
        <Input type="text" id="AISN_Group" value={aisnGroup} disabled={true} />
      </InputDataDiv>
    );
  };

  const renderInputStartingDate = () => {
    const enabledDates = (date) => {
      return moment(date).isAfter(moment(), "day");
    };

    const handleSetDate = (date) => {
      setStartingDate(date);
      setStartingDateError(false);
    };

    return (
      <InputDataDiv>
        <Label htmlFor="startingDate" fontWeight="bold">
          {t(aisnGroup === "Control" ? "Starting date" : "RGS+ Starting date")} *
        </Label>
        <InputStartingDateDiv>
          <InputStartingDate
            selected={startingDate}
            onChange={(date) => handleSetDate(date)}
            filterDate={enabledDates}
            placeholderText="dd/mm/yyyy"
            dateFormat={"dd/MM/yyyy"}
            border={startingDateError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
            disabled={disabledInputs || isLessThanToday(currentStartingDate)}
          />
        </InputStartingDateDiv>
      </InputDataDiv>
    );
  };

  return (
    <PersonalInformationDiv>
      <TitleTextDiv>
        <p>{t("personalInformation_title")}</p>
      </TitleTextDiv>
      <DataDiv>
        <ColumnDataDiv width="385px">
          <InputDataDiv>
            <Label htmlFor="login" fontWeight="bold">
              {isAISNHospital ? "Patient ID" : t("username")} *
            </Label>
            <Input type="text" placeholder={t("personalInformation_userNameInput")} id="login" value={user} disabled={true} />
          </InputDataDiv>
          <InputDataDiv>
            <Label htmlFor="password" fontWeight="bold">
              {t("password")} *
            </Label>
            <Input
              type={visiblePassword ? "text" : "password"}
              placeholder="*******"
              id="password"
              value={password}
              onChange={(e) => handleShowPassword(e, setPassword, setPasswordError)}
              error={passwordError}
              padding="5px 32px 5px 10px"
              disabled={disabledInputs}
            />
            {showButtonPassword && (
              <RelativeButton onClick={handleSetPasswordVisibility}>
                <img src={visiblePassword ? HidePasswordImg : VisiblePasswordImg} alt="icon-password-button" />
              </RelativeButton>
            )}
          </InputDataDiv>
          <InputDataDiv>
            <Label fontWeight="bold">{t("therapist")} *</Label>
            <Select
              id="clinicianId"
              value={clinicianId}
              onChange={(e) => handleShowPassword(e, setClinicianId, setClinicianIdError)}
              error={clinicianIdError}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={disabledInputs}
            >
              <option value=""></option>
              {hospitalClinicians.map((clinician) => {
                const clinicianId = clinician.CLINICIAN_ID;
                return (
                  <option key={clinicianId} value={clinicianId}>
                    {clinician.CLINICIAN_USER}
                  </option>
                );
              })}
            </Select>
          </InputDataDiv>
          {isAISNHospital && (
            <>
              {renderSelectAISNGroup()}
              {renderInputStartingDate()}
            </>
          )}
          <InputDataDiv>
            <Label fontWeight="bold">{t("patientGender_label")} *</Label>
            <Select
              value={gender}
              onChange={(e) => handleSetInput(e, setGender, setGenderError, "gender")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              error={genderError}
              disabled={disabledInputs}
            >
              <option value="MALE">{t("patientGender_male")}</option>
              <option value="FEMALE">{t("patientGender_female")}</option>
            </Select>
          </InputDataDiv>
          {project === "global" && (
            <InputDataDiv>
              <Label htmlFor="height" fontWeight="bold">
                {t("personalInformation_heightLabel")} *
              </Label>
              <Input
                type="number"
                placeholder="175"
                id="height"
                value={height}
                onChange={(e) => handleSetInput(e, setHeight, setHeightError, "height")}
                error={heightError}
                disabled={disabledInputs}
              />
            </InputDataDiv>
          )}
          <InputDataDiv>
            <Label htmlFor="dateOfBirth" fontWeight="bold">
              {t("personalInformation_birthdayLabel")} *
            </Label>
            <InputBirthday
              format="##/##/####"
              placeholder="dd/mm/yyyy"
              mask={["d", "d", "m", "m", "y", "y", "y", "y"]}
              value={dateOfBirth}
              onChange={(e) => handleSetInput(e, setDateOfBirth, setDateOfBirthError, "birthDate")}
              error={dateOfBirthError ? `1px solid ${errorColor}` : `1px solid ${bodyColor}`}
              disabled={disabledInputs}
            />
          </InputDataDiv>

          {isAISNHospital && (
            <InputDataDiv>
              <Label fontWeight="bold">{t("Civil status")} *</Label>
              <Select
                value={civilStatus}
                onChange={(e) => handleSetInput(e, setCivilStatus, setCivilStatusError, "civilStatus")}
                padding="5px 35px 5px 10px"
                width="200px"
                arrowImg={SelectArrow}
                backgroundPosition="175px center"
                error={civilStatusError}
                disabled={disabledInputs}
              >
                {renderCivilStatusOptions(t)}
              </Select>
            </InputDataDiv>
          )}

          {project === "strack" && renderSkinColor()}
        </ColumnDataDiv>
        <ColumnDataDiv width="385px">
          {isAISNHospital && (
            <>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Home support")} *</Label>
                <Select
                  value={homeSupport}
                  onChange={(e) => handleSetInput(e, setHomeSupport, setHomeSupportError, "homeSupport")}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={homeSupportError}
                  disabled={disabledInputs}
                >
                  <option value=""></option>
                  <option value={0}>{t("no")}</option>
                  <option value={1}>{t("yes")}</option>
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Education")} *</Label>
                <Select
                  value={education}
                  onChange={(e) => handleSetInput(e, setEducation, setEducationError, "education")}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={educationError}
                  disabled={disabledInputs}
                >
                  {renderEducationOptions(t)}
                </Select>
              </InputDataDiv>
              <InputDataDiv>
                <Label fontWeight="bold">{t("Work")} *</Label>
                <Select
                  value={work}
                  onChange={(e) => handleSetInput(e, setWork, setWorkError, "work")}
                  padding="5px 35px 5px 10px"
                  width="200px"
                  arrowImg={SelectArrow}
                  backgroundPosition="175px center"
                  error={workError}
                  disabled={disabledInputs}
                >
                  {renderWorkOptions(t)}
                </Select>
              </InputDataDiv>
            </>
          )}
          <InputDataDiv>
            <Label>{t("personalInformation_videoGameExperienceLabel")}</Label>
            <Select
              value={gameExperience}
              onChange={(e) => handleSetInput(e, setGameExperience, false, "videoGameExp")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={disabledInputs}
            >
              <option value={0}>{t("no")}</option>
              <option value={1}>{t("yes")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv>
            <Label>{t("personalInformation_computerExperienceLabel")}</Label>
            <Select
              value={computerExperience}
              onChange={(e) => handleSetInput(e, setComputerExperience, false, "computerExp")}
              padding="5px 35px 5px 10px"
              width="200px"
              arrowImg={SelectArrow}
              backgroundPosition="175px center"
              disabled={disabledInputs}
            >
              <option value={0}>{t("no")}</option>
              <option value={1}>{t("yes")}</option>
            </Select>
          </InputDataDiv>
          <InputDataDiv alignItems="flex-start">
            <Label htmlFor="comment">{t("personalInformation_otherInformationLabel")}</Label>
            <Textarea
              id="comment"
              value={otherInfo}
              onChange={(e) => handleSetInput(e, setOtherInfo, false, "comment")}
              disabled={disabledInputs}
            />
          </InputDataDiv>
          {project === "global" && renderSkinColor()}
        </ColumnDataDiv>
      </DataDiv>
    </PersonalInformationDiv>
  );
};

export default PersonalInfo;
